import { Inject, Injectable } from '@angular/core';

import { AuthService, OidcAuthService, RidangoEpic, RidangoReduxAction, ViispTokenResponse } from '@ridango/core';

import { ActionsObservable } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, ignoreElements, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Authenticator, SessionService } from '../../services';
import { CustomerAuthActions } from '../customer-auth';
import { RecoverPasswordActions } from '../recover-password';
import { LoginActions } from './login.actions';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SavePasswordService } from '@ridango/core/lib/services/save-password.service';
import { afterLoginSuccess } from '../../../permissions';
import { Store } from '@ngrx/store';
import Env from '@ridango/core/env';
import { Preferences } from '@capacitor/preferences';

@Injectable({
    providedIn: 'root'
})
export class LoginEpics implements RidangoEpic {
    constructor(
        private actions: LoginActions,
        private authService: AuthService,
        private customerAction: CustomerAuthActions,
        private auth: Authenticator,
        private session: SessionService,
        private oidcAuthService: OidcAuthService,
        private router: Router,
        private translate: TranslateService,
        private savePasswordService: SavePasswordService,
        private store$: Store,
        @Inject('ENV') private env: Env
    ) {}

    register() {
        return [
            this.login,
            this.loginEmail,
            this.loginSuccess,
            this.loginAfterAgree,
            this.logout,
            this.loginAuthTokenOnly,
            this.loginIdCard,
            this.loginMobileId,
            this.loginSmartId,
            this.loginViisp,
            this.loginOidcExchange,
            this.logoutHook
        ];
    }

    login = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN).pipe(
            filter(() => !this.env.WMB?.AUTH),
            switchMap((action) => {
                const { email, password, lang, ondemandRequest, rememberMe } = action.payload;
                return this.auth.submitCredentials(email, password, lang).pipe(
                    tap(() => {
                        this.auth.setRememberMeValue(rememberMe ? email : null);
                        this.savePasswordService.promptSaveCredentials(email, password);
                    }),
                    map((response) => this.actions.loginCredentialsSuccess(response.access_token, ondemandRequest)),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    loginEmail = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN).pipe(
            filter(() => !!this.env.WMB?.AUTH),
            switchMap((action) => {
                const { email, password, rememberMe } = action.payload;
                return this.auth.submitEmailCredentials(email, password).pipe(
                    switchMap(async (response) => {
                        this.auth.setRememberMeValue(rememberMe ? email : null);

                        // Need to set in localStorage because of older code checks
                        localStorage.setItem(`${this.env.APP_NAME}-access`, response.accessToken);
                        localStorage.setItem(`${this.env.APP_NAME}-access-user`, email);

                        await Preferences.set({
                            key: `${this.env.APP_NAME}-access`,
                            value: response.accessToken
                        });
                        await Preferences.set({
                            key: `${this.env.APP_NAME}-refresh-token`,
                            value: response.refreshToken
                        });
                        await Preferences.set({ key: `${this.env.APP_NAME}-token-type`, value: response.tokenType });
                        this.store$.dispatch(afterLoginSuccess());
                        return this.actions.loginSuccess();
                    }),
                    catchError((e) => of(this.actions.loginFailed({ error: { error: e.error?.message } })))
                );
            })
        );

    loginAuthTokenOnly = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN_AUTH_TOKEN_ONLY).pipe(
            switchMap((action) => {
                const { email, password, lang } = action.payload;
                return this.auth.submitCredentials(email, password, lang).pipe(
                    map((response) => {
                        this.savePasswordService.promptSaveCredentials(email, password);
                        this.actions.loginSuccessAuthTokenOnly(response.access_token);
                    }),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    loginIdCard = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN_ID_CARD).pipe(
            switchMap((action) => {
                const { token, nonce } = action.payload;
                return this.auth.submitIdCardAuth(token, nonce).pipe(
                    switchMap((response) => this.handleLoginSuccess(response)),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    loginMobileId = (actions$: ActionsObservable<any>) =>
        actions$.ofType(LoginActions.LOGIN_MOBILE_ID).pipe(
            switchMap((action) => {
                const challengePollActionFn = this.actions.loginWithMobileId;
                return this.handleAuthChallengeStatusPoll(action, actions$, 'mid', challengePollActionFn);
            })
        );

    loginSmartId = (actions$: ActionsObservable<any>) =>
        actions$.ofType(LoginActions.LOGIN_SMART_ID).pipe(
            switchMap((action) => {
                const challengePollActionFn = this.actions.loginWithSmartId;
                return this.handleAuthChallengeStatusPoll(action, actions$, 'smartid', challengePollActionFn);
            })
        );

    loginViisp = (actions$: ActionsObservable<any>) =>
        actions$.ofType(LoginActions.LOGIN_VIISP).pipe(
            switchMap((action) => {
                const { ticket } = action.payload;
                return this.auth.fetchViispToken(ticket).pipe(
                    switchMap((response: ViispTokenResponse) => this.handleLoginSuccess(response)),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    loginOidcExchange = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN_OIDC_EXCHANGE).pipe(
            switchMap((action) => {
                const { exchangeId } = action.payload;
                return this.oidcAuthService.exchange(exchangeId).pipe(
                    switchMap((response) => this.handleOidcLoginSuccess(response)),
                    catchError((exchangeError) => {
                        return of(this.actions.loginFailed(exchangeError));
                    })
                );
            })
        );

    loginSuccess = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN_CREDENTIAL_SUCCESS, RecoverPasswordActions.SET_NEW_PASSWORD_SUCCESS).pipe(
            switchMap((action) => {
                const { token, ondemandRequest } = action.payload;
                return this.auth.fetchAccessToken('ridango/auth', token, null, null, ondemandRequest).pipe(
                    switchMap((response) => this.handleLoginSuccess(response)),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    loginAfterAgree = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGIN_AFTER_AGREE).pipe(
            switchMap((action) => {
                const { terms, reason, ondemandRequest, token } = action.payload;
                return this.auth.updateAgreeTerms(terms.id, reason, ondemandRequest).pipe(
                    switchMap((response: any) => {
                        if (!token && response.token) {
                            return this.handleLoginSuccess(response);
                        }

                        return this.auth
                            .fetchAccessToken('ridango/auth', token, null, null, ondemandRequest)
                            .pipe(switchMap((response) => this.handleLoginSuccess(response)));
                    }),
                    catchError((error) => of(this.actions.loginFailed(error)))
                );
            })
        );

    logout = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGOUT).pipe(
            map((action) => {
                const { shouldRedirect } = action.payload;

                this.session.signOut();
                shouldRedirect && this.router.navigate([this.translate.currentLang, 'login']);
            }),
            ignoreElements()
        );

    logoutHook = (action$: ActionsObservable<any>) =>
        action$.ofType(LoginActions.LOGOUT).pipe(
            filter(() => !!this.env.WMB?.AUTH),
            switchMap(async () => {
                const refreshToken = await Preferences.get({
                    key: `${this.env.APP_NAME}-refresh-token`
                });

                localStorage.removeItem(`${this.env.APP_NAME}-access`);
                localStorage.removeItem(`${this.env.APP_NAME}-access-user`);

                await Preferences.remove({
                    key: `${this.env.APP_NAME}-access`
                });
                await Preferences.remove({
                    key: `${this.env.APP_NAME}-refresh-token`
                });
                await Preferences.remove({ key: `${this.env.APP_NAME}-token-type` });
                return refreshToken.value;
            }),
            switchMap((token) => {
                return this.auth.endSession(token);
            }),
            ignoreElements()
        );

    private handleLoginSuccess(customerData) {
        this.session.setLocalAccessToken(customerData);
        this.store$.dispatch(afterLoginSuccess());
        return [this.customerAction.loadCustomerData(customerData), this.actions.loginSuccess()];
    }

    private handleOidcLoginSuccess(response: { accessToken: string; refreshToken: string; tokenType: string }) {
        return from(this.persistOidcTokens(response)).pipe(
            switchMap(() =>
                this.handleLoginSuccess({
                    token: response.accessToken
                })
            ),
            catchError((persistError) => of(this.actions.loginFailed(persistError)))
        );
    }

    private async persistOidcTokens(response: {
        accessToken: string;
        refreshToken: string;
        tokenType: string;
    }): Promise<void> {
        localStorage.setItem(`${this.env.APP_NAME}-access`, response.accessToken);
        await Preferences.set({
            key: `${this.env.APP_NAME}-access`,
            value: response.accessToken
        });
        await Preferences.set({
            key: `${this.env.APP_NAME}-refresh-token`,
            value: response.refreshToken
        });
        await Preferences.set({
            key: `${this.env.APP_NAME}-token-type`,
            value: response.tokenType
        });
    }

    private handleAuthChallengeStatusPoll(
        action: RidangoReduxAction,
        actions$: ActionsObservable<any>,
        authMethod: string,
        challengePollActionFn: any
    ): Observable<any> {
        const { verificationCode, hash } = action.payload;
        return this.auth.checkAuthChallengeStatus(authMethod, hash).pipe(
            takeUntil(actions$.ofType(LoginActions.CANCEL_CHALLENGE_POLLING)),
            switchMap((response) =>
                this.handleLoginSuccessOrPoll(response, verificationCode, hash, challengePollActionFn)
            ),
            catchError((error) => of(this.actions.loginFailed(error)))
        );
    }

    private handleLoginSuccessOrPoll(
        response: any,
        verificationCode: string,
        hash: string,
        challengePollActionFn: any
    ): RidangoReduxAction[] {
        if (response.state === 'COMPLETE') {
            return this.handleLoginSuccess(response);
        } else {
            return [challengePollActionFn(verificationCode, hash)];
        }
    }
}
